/** Pure camera / field-gesture helpers (no Phaser). */

export const FIELD_DRAG_PX = 12;

export type FieldGestureState = {
  active: boolean;
  startX: number;
  startY: number;
  lastX: number;
  lastY: number;
  camScrollX: number;
  camScrollY: number;
  dragged: boolean;
};

export function beginFieldGesture(
  x: number,
  y: number,
  camScrollX: number,
  camScrollY: number,
): FieldGestureState {
  return {
    active: true,
    startX: x,
    startY: y,
    lastX: x,
    lastY: y,
    camScrollX,
    camScrollY,
    dragged: false,
  };
}

export function updateFieldGesture(
  g: FieldGestureState,
  x: number,
  y: number,
  dragPx = FIELD_DRAG_PX,
): { dx: number; dy: number; becameDrag: boolean } {
  const dx = x - g.lastX;
  const dy = y - g.lastY;
  g.lastX = x;
  g.lastY = y;
  const dist = Math.hypot(x - g.startX, y - g.startY);
  const becameDrag = !g.dragged && dist >= dragPx;
  if (becameDrag) g.dragged = true;
  return { dx, dy, becameDrag };
}

export function endFieldGesture(g: FieldGestureState): {
  isTap: boolean;
  x: number;
  y: number;
} {
  g.active = false;
  return { isTap: !g.dragged, x: g.lastX, y: g.lastY };
}

/**
 * Expand map camera bounds by HUD chrome (game-pixel insets / zoom → world pad)
 * so map edges can be panned into the mid band between top/bottom UI.
 */
export function cameraBoundsWithHudInset(
  mapW: number,
  mapH: number,
  zoom: number,
  insetTopPx: number,
  insetBottomPx: number,
  insetLeftPx = 0,
  insetRightPx = 0,
): { x: number; y: number; width: number; height: number } {
  const z = Math.max(0.05, zoom);
  const padT = Math.max(0, insetTopPx) / z;
  const padB = Math.max(0, insetBottomPx) / z;
  const padL = Math.max(0, insetLeftPx) / z;
  const padR = Math.max(0, insetRightPx) / z;
  return {
    x: padL > 0 ? -padL : 0,
    y: padT > 0 ? -padT : 0,
    width: mapW + padL + padR,
    height: mapH + padT + padB,
  };
}

/** CSS px → Phaser game pixels using canvas display scale. */
export function cssPxToGamePx(
  cssPx: number,
  canvasCssSize: number,
  gameSize: number,
): number {
  if (canvasCssSize <= 0 || gameSize <= 0) return cssPx;
  return cssPx * (gameSize / canvasCssSize);
}

/** CSS #rrggbb from 0xRRGGBB. */
export function hexToCss(color: number): string {
  const c = color >>> 0;
  return `#${(c & 0xffffff).toString(16).padStart(6, "0")}`;
}

/** Re-export engine pinch math for FFA camera callers. */
export {
  PINCH_MIN_START_DIST,
  pinchScale,
  pointerPairDistance,
} from "../../../input/pinch-tracker";

export function resolveFloatColor(
  presetColor: string,
  vars?: Record<string, string | number>,
): string {
  const override = vars?.color;
  if (typeof override === "string" && override.length > 0) return override;
  return presetColor;
}
